import React from "react";
import InputMask from "react-input-mask";
import { Box, Typography } from "@mui/material";
import { Input, Select } from "antd";
import DocumentField from "./DocumentField";
import {
  BODYWORK_TYPES,
  RNTRC_TYPE_OPTIONS,
  STATES_UF,
} from "./vehicle-form-constants";

function getSelectPopupContainer(trigger) {
  return (
    trigger?.closest?.(".MuiDialog-root") ||
    trigger?.closest?.(".reboque-editor-card") ||
    document.body
  );
}

function Field({ label, children, span = 3, error, fieldPath }) {
  const errorId = fieldPath
    ? `vehicle-field-${String(fieldPath).replace(/\./g, "-")}-error`
    : undefined;

  return (
    <div
      className={`new-vehicle-field new-vehicle-span-${span}${error ? " has-error" : ""}`}
      data-field-path={fieldPath || undefined}
    >
      <label>{label}</label>
      {children}
      {error ? (
        <span id={errorId} role="alert" className="new-vehicle-field-error">
          {error}
        </span>
      ) : null}
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <Typography
      component="h3"
      className="reboque-form-section-title new-vehicle-span-12"
    >
      {children}
    </Typography>
  );
}

/**
 * Campos do formulário de reboque (create/edit), reutilizáveis.
 * pathPrefix: ex. "" ou "reboques.0" para data-field-path / erros.
 * Layout visual apenas — handlers, máscaras e valores permanecem inalterados.
 */
function ReboqueFormFields({
  value,
  onChange,
  fieldErrors = {},
  pathPrefix = "",
  uploading = false,
  onUploadAntt,
  disabled = false,
}) {
  const reboque = value || {};
  const path = (field) => (pathPrefix ? `${pathPrefix}.${field}` : field);
  const getError = (field) => fieldErrors[path(field)] || fieldErrors[field] || "";

  const setField = (field, next) => {
    onChange?.({ ...reboque, [field]: next });
  };

  return (
    <Box className="new-vehicle-grid reboque-form-fields">
      <SectionTitle>Identificação</SectionTitle>

      <Field
        label="Placa Reboque *"
        span={3}
        fieldPath={path("placaReboque")}
        error={getError("placaReboque")}
      >
        <InputMask
          mask="aaa-9*99"
          maskChar=""
          className="form-control"
          disabled={disabled}
          value={reboque.placaReboque || ""}
          onChange={(e) => setField("placaReboque", e.target.value.toUpperCase())}
        >
          {(inputProps) => (
            <input
              {...inputProps}
              aria-invalid={Boolean(getError("placaReboque"))}
            />
          )}
        </InputMask>
      </Field>

      <Field
        label="Renavam Reboque *"
        span={3}
        fieldPath={path("renavamReboque")}
        error={getError("renavamReboque")}
      >
        <Input
          maxLength={11}
          disabled={disabled}
          status={getError("renavamReboque") ? "error" : undefined}
          value={reboque.renavamReboque || ""}
          onChange={(e) =>
            setField("renavamReboque", e.target.value.replace(/\D/g, ""))
          }
        />
      </Field>

      <Field
        label="UF Reboque *"
        span={2}
        fieldPath={path("ufReboque")}
        error={getError("ufReboque")}
      >
        <Select
          style={{ width: "100%" }}
          disabled={disabled}
          status={getError("ufReboque") ? "error" : undefined}
          value={reboque.ufReboque || undefined}
          placeholder="Selecione"
          onChange={(v) => setField("ufReboque", v || "")}
          options={STATES_UF.map((s) => ({
            value: s.value,
            label: `${s.label} (${s.value})`,
          }))}
          showSearch
          optionFilterProp="label"
          allowClear
          getPopupContainer={getSelectPopupContainer}
          dropdownStyle={{ zIndex: 2000 }}
        />
      </Field>

      <Field label="Descrição Reboque" span={4}>
        <Input
          disabled={disabled}
          value={reboque.descricaoReboque || ""}
          onChange={(e) => setField("descricaoReboque", e.target.value)}
        />
      </Field>

      <SectionTitle>RNTRC / Carroceria</SectionTitle>

      <Field
        label="RNTRC Reboque *"
        span={4}
        fieldPath={path("rntrcReboque")}
        error={getError("rntrcReboque")}
      >
        <Input
          maxLength={8}
          disabled={disabled}
          status={getError("rntrcReboque") ? "error" : undefined}
          value={reboque.rntrcReboque || ""}
          onChange={(e) =>
            setField("rntrcReboque", e.target.value.replace(/\D/g, ""))
          }
        />
      </Field>

      <Field
        label="Tipo RNTRC Reboque *"
        span={4}
        fieldPath={path("tipoRNTRCReboque")}
        error={getError("tipoRNTRCReboque")}
      >
        <Select
          style={{ width: "100%" }}
          disabled={disabled}
          status={getError("tipoRNTRCReboque") ? "error" : undefined}
          value={reboque.tipoRNTRCReboque || undefined}
          placeholder="Selecione"
          onChange={(v) => setField("tipoRNTRCReboque", v || "")}
          options={RNTRC_TYPE_OPTIONS}
          allowClear
          getPopupContainer={getSelectPopupContainer}
          dropdownStyle={{ zIndex: 2000 }}
        />
      </Field>

      <Field
        label="Tipo Carroceria Reboque *"
        span={4}
        fieldPath={path("TipoCarroceriaReboque")}
        error={getError("TipoCarroceriaReboque")}
      >
        <Select
          style={{ width: "100%" }}
          disabled={disabled}
          status={getError("TipoCarroceriaReboque") ? "error" : undefined}
          value={reboque.TipoCarroceriaReboque || undefined}
          placeholder="Selecione"
          onChange={(v) => setField("TipoCarroceriaReboque", v || "")}
          options={BODYWORK_TYPES.map((t) => ({ value: t, label: t }))}
          allowClear
          getPopupContainer={getSelectPopupContainer}
          dropdownStyle={{ zIndex: 2000 }}
        />
      </Field>

      <SectionTitle>Proprietário</SectionTitle>

      <Field label="Tipo Proprietário Reboque" span={3}>
        <Select
          style={{ width: "100%" }}
          disabled={disabled}
          value={reboque.tipoProprietarioReboque || undefined}
          onChange={(v) => setField("tipoProprietarioReboque", v || "")}
          options={[
            { value: "cpf", label: "CPF" },
            { value: "cnpj", label: "CNPJ" },
          ]}
          getPopupContainer={getSelectPopupContainer}
          dropdownStyle={{ zIndex: 2000 }}
        />
      </Field>

      <Field label="CPF/CNPJ Proprietário Reboque" span={4}>
        <InputMask
          mask={
            reboque.tipoProprietarioReboque === "cnpj"
              ? "99.999.999/9999-99"
              : "999.999.999-99"
          }
          maskChar=""
          disabled={disabled}
          value={reboque.cpfoucnpjReboque || ""}
          onChange={(e) => setField("cpfoucnpjReboque", e.target.value)}
        >
          {(inputProps) => <Input {...inputProps} disabled={disabled} />}
        </InputMask>
      </Field>

      <Field label="Nome Proprietário Reboque" span={5}>
        <Input
          disabled={disabled}
          value={reboque.nomeProprietarioReboque || ""}
          onChange={(e) => setField("nomeProprietarioReboque", e.target.value)}
        />
      </Field>

      <SectionTitle>Documento</SectionTitle>

      <Field
        label="ANTT Reboque *"
        span={12}
        fieldPath={path("anttReboque")}
        error={getError("anttReboque")}
      >
        <DocumentField
          url={reboque.anttReboque}
          disabled={disabled || uploading}
          error={Boolean(getError("anttReboque"))}
          errorId={`vehicle-field-${path("anttReboque").replace(/\./g, "-")}-error`}
          onUpload={(file) => onUploadAntt?.(file)}
          onClear={() => setField("anttReboque", "")}
        />
      </Field>
    </Box>
  );
}

export default ReboqueFormFields;
